import { cva, type VariantProps } from 'class-variance-authority'
import { format, isAfter, isBefore, isSameDay } from 'date-fns'
import { type MouseEvent, useEffect, useState } from 'react'
import type { DateRange, OnSelectHandler } from 'react-day-picker'
import { cn } from '../../lib'
import { Button } from '../ui/button'
import { Calendar } from '../ui/calendar'
import { Icon } from '../ui/icon'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Text } from '../ui/text'

const triggerVariants = cva('relative p-4 cursor-pointer', {
  variants: {
    variant: {
      default: 'bg-input-bg border-border',
      inverse: 'bg-input-bg-inverse border-border-inverse',
      'inverse-bare-bones': 'bg-sub-screen-bg-inverse border-transparent',
    },
  },
  defaultVariants: { variant: 'default' },
})

const chipVariants = cva('flex items-center justify-center gap-2 rounded-sm py-2 pl-2 pr-4', {
  variants: {
    variant: {
      default: 'bg-sub-surface-bg',
      inverse: 'bg-sub-surface-bg-inverse',
      'inverse-bare-bones': 'bg-sub-screen-bg-inverse',
    },
  },
  defaultVariants: { variant: 'default' },
})

const valueTextVariants = cva('font-number font-medium', {
  variants: {
    variant: { default: '', inverse: '', 'inverse-bare-bones': '' },
    filled: { true: '', false: '' },
  },
  compoundVariants: [
    { variant: 'default', filled: true, class: 'text-header' },
    { variant: 'default', filled: false, class: 'text-secondary' },
    { variant: 'inverse', filled: true, class: 'text-header-inverse' },
    { variant: 'inverse', filled: false, class: 'text-secondary-inverse' },
    {
      variant: 'inverse-bare-bones',
      filled: true,
      class: 'text-header-inverse',
    },
    {
      variant: 'inverse-bare-bones',
      filled: false,
      class: 'text-secondary-inverse',
    },
  ],
  defaultVariants: { variant: 'default', filled: false },
})

const iconVariants = cva('size-6', {
  variants: {
    variant: {
      default: 'text-primary-accent',
      inverse: 'text-primary-inverse',
      'inverse-bare-bones': 'text-primary-accent',
    },
  },
  defaultVariants: { variant: 'default' },
})

/**
 * Adds a clicked day to the range. Unlike react-day-picker's `addToRange`, a
 * click completes the range only when a start is already chosen.
 */
function addDayToRange(day: Date, { from, to }: { from?: Date; to?: Date }) {
  if (from && to) {
    if (isSameDay(to, day) && isSameDay(from, day)) return {}
    if (isSameDay(to, day)) return { from: to }
    if (isSameDay(from, day)) return {}
    if (isAfter(from, day)) return { from: day, to }
    return { from, to: day }
  }
  if (to) {
    if (isAfter(day, to)) return { from: to, to: day }
    return { from: day, to }
  }
  if (from) {
    if (isBefore(day, from)) return { from: day, to: from }
    return { from, to: day }
  }
  return { from: day }
}

type DatePickerVariant = NonNullable<VariantProps<typeof triggerVariants>['variant']>

type DatePickerWithRangeProps = {
  onChange: (e: { from?: Date; to?: Date }) => void
  selected: { from?: Date; to?: Date }
  open?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  disabledDates?: { before?: Date; after?: Date }
  displayFormat?: string
  placeholder?: string
  textClassName?: string
  className?: string
  buttonClassName?: string
  iconClassName?: string
  variant?: DatePickerVariant
  wrapperClassName?: string
  appearance?: 'input' | 'chip' | 'single-input'
}

export function DatePickerWithRange({
  onChange,
  selected,
  open,
  onOpenChange,
  disabled = false,
  disabledDates,
  displayFormat = 'LLL dd, y',
  placeholder = 'Select a date range',
  textClassName = '',
  className = '',
  buttonClassName = '',
  iconClassName = '',
  variant = 'default',
  wrapperClassName = '',
  appearance = 'input',
}: DatePickerWithRangeProps) {
  const [fromDate, setFromDate] = useState<Date | undefined>(selected?.from)
  const [toDate, setToDate] = useState<Date | undefined>(selected?.to)

  const isFilled = fromDate != null && toDate != null

  const handleChange: OnSelectHandler<DateRange | undefined> = (_range, triggerDate, _modifiers, event) => {
    event.preventDefault()

    const next = addDayToRange(triggerDate, { from: fromDate, to: toDate })
    setFromDate(next.from)
    setToDate(next.to)
    onChange(next)
  }

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (disabled) return
    setFromDate(undefined)
    setToDate(undefined)
    onChange({})
  }

  useEffect(() => {
    if (selected) {
      setFromDate(selected.from)
      setToDate(selected.to)
    }
  }, [selected])

  const disabledMatcher = (() => {
    const { before, after } = disabledDates || {}
    if (before && after) {
      return { before, after }
    }
    if (before) {
      return { before }
    }
    if (after) {
      return { after }
    }
  })()

  const renderChipTrigger = () => {
    if (isFilled) {
      return (
        <div
          className={cn(
            'flex items-center gap-2 rounded-full border border-border bg-sub-screen-bg pl-3 pr-2',
            buttonClassName,
          )}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="date-picker-button"
              id="date"
              disabled={disabled}
              className="flex cursor-pointer items-center gap-2 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icon name="Calendar" className={cn('size-3 text-primary-accent', iconClassName)} />
              <Text variant="xs-sb" className={cn('text-header', textClassName)}>
                {`${format(fromDate, displayFormat)} - ${format(toDate, displayFormat)}`}
              </Text>
            </button>
          </PopoverTrigger>
          <button
            type="button"
            data-testid="date-picker-clear"
            aria-label="Clear date range"
            disabled={disabled}
            className="flex shrink-0 cursor-pointer items-center p-1 disabled:cursor-not-allowed disabled:opacity-50"
            onMouseDown={event => {
              event.preventDefault()
              event.stopPropagation()
            }}
            onClick={handleClear}
          >
            <Icon name="X" className="size-3 text-secondary" />
          </button>
        </div>
      )
    }

    return (
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="date-picker-button"
          id="date"
          disabled={disabled}
          className={cn(
            'flex h-[33px] cursor-pointer items-center gap-2 rounded-md border border-border bg-sub-surface-bg px-4 disabled:cursor-not-allowed disabled:opacity-50',
            buttonClassName,
          )}
        >
          <Text variant="xs-sb" className={cn('text-header', textClassName)}>
            {placeholder}
          </Text>
        </button>
      </PopoverTrigger>
    )
  }

  const renderInputTrigger = () => {
    if (appearance !== 'single-input' && isFilled) {
      return (
        <div className={cn('flex items-center gap-2', buttonClassName)}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="date-picker-button"
              disabled={disabled}
              className={cn(
                chipVariants({ variant }),
                'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              <Icon name="CalendarFold" className={cn(iconVariants({ variant }), iconClassName)} />
              <Text className={cn(valueTextVariants({ variant, filled: true }), textClassName)}>
                {format(fromDate, displayFormat)}
              </Text>
            </button>
          </PopoverTrigger>

          <div className={cn(chipVariants({ variant }), 'size-10 shrink-0 p-2')} aria-hidden>
            <Icon name="ArrowRight" className={cn(iconVariants({ variant }))} />
          </div>

          <div className={cn(chipVariants({ variant }))}>
            <button
              type="button"
              data-testid="date-picker-clear"
              aria-label="Clear date range"
              disabled={disabled}
              className="shrink-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              onMouseDown={event => {
                event.preventDefault()
                event.stopPropagation()
              }}
              onClick={handleClear}
            >
              <Icon name="CalendarOff" className={cn(iconVariants({ variant }), iconClassName)} />
            </button>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Text className={cn(valueTextVariants({ variant, filled: true }), textClassName)}>
                  {format(toDate, displayFormat)}
                </Text>
              </button>
            </PopoverTrigger>
          </div>
        </div>
      )
    }

    const displayValue =
      appearance === 'single-input' && fromDate && toDate
        ? `${format(fromDate, displayFormat)} - ${format(toDate, displayFormat)}`
        : fromDate
          ? format(fromDate, displayFormat)
          : placeholder

    return (
      <PopoverTrigger asChild>
        <Button
          data-testid="date-picker-button"
          id="date"
          variant="bordered"
          disabled={disabled}
          className={cn(triggerVariants({ variant }), buttonClassName)}
        >
          <div className="flex-1">
            <Text
              className={cn(
                valueTextVariants({
                  variant,
                  filled: Boolean(fromDate),
                }),
                textClassName,
              )}
            >
              {displayValue}
            </Text>
          </div>
          <Icon name="Calendar" className={cn(iconVariants({ variant }), 'size-5', iconClassName)} />
        </Button>
      </PopoverTrigger>
    )
  }

  return (
    <div className={cn('flex items-center', className)}>
      <Popover open={open} onOpenChange={onOpenChange}>
        {appearance === 'chip' ? renderChipTrigger() : renderInputTrigger()}

        <PopoverContent className={cn('w-auto p-0', wrapperClassName)} align="start">
          <Calendar
            data-testid="date-picker-calendar"
            disabled={disabledMatcher}
            mode="range"
            defaultMonth={fromDate}
            selected={{ from: fromDate, to: toDate }}
            onSelect={handleChange}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
